/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TrashIcon } from '@radix-ui/react-icons';
import { Text, Textarea, UnstyledButton } from '@/shared/ui';
import { usePropertyNotes } from '../../hooks/usePropertyNotes';
import { SectionCard } from './SectionCard';
import { PropertyDialog } from './PropertyDialog';
import { formatNoteAuthor, formatNoteDate } from './note-utils';

interface PropertyNotesSectionProps {
  agencyId: string;
  propertyId: string;
}

/**
 * Free-text notes about a listing, written from a dialog rather than an
 * inline field.
 *
 * A note here is usually a paragraph — what the doorman said, what the owner
 * will really take — not a one-liner, and an always-open input on a screen
 * this dense reads as another form to fill. Behind a button the section shows
 * only what was written, and the dialog gives the note room to be written in.
 *
 * @param props - The agency and property identifiers.
 * @returns The rendered notes section.
 */
export const PropertyNotesSection: React.FC<PropertyNotesSectionProps> = ({
  agencyId,
  propertyId,
}) => {
  const { t } = useTranslation('features/properties');
  const { notes, isLoading, addNote, deleteNote } = usePropertyNotes({ agencyId, propertyId });
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const closeDialog = () => {
    setComposing(false);
    setDraft('');
  };

  const handleSave = async () => {
    setSaving(true);
    const ok = await addNote(draft);
    setSaving(false);
    if (!ok) {
      toast.error(t('detailView.notes.addError'));
      return;
    }
    closeDialog();
  };

  const handleDelete = async (noteId: string) => {
    const ok = await deleteNote(noteId);
    if (!ok) toast.error(t('detailView.notes.deleteError'));
  };

  return (
    <>
      <SectionCard
        title={t('detailView.notes.title')}
        data-testid="property-notes-section"
        titleAccessory={
          <UnstyledButton
            type="button"
            onClick={() => setComposing(true)}
            data-testid="property-note-add"
            className="whitespace-nowrap text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-[var(--seed-base)] hover:underline underline-offset-2"
          >
            {t('detailView.notes.add')}
          </UnstyledButton>
        }
      >
        {isLoading ? (
          <div className="flex flex-col gap-[var(--space-2)]">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-[64px] animate-pulse rounded-(--radius-md) bg-[var(--surface-tertiary)]" />
            ))}
          </div>
        ) : notes.length === 0 ? (
          <div className="flex h-[80px] items-center justify-center text-[length:var(--text-body-sm)] text-[var(--text-tertiary)]">
            {t('detailView.notes.empty')}
          </div>
        ) : (
          <div className="flex flex-col gap-[var(--space-3)]">
            {notes.map((note) => (
              <div
                key={note.note_id}
                data-testid="property-note"
                className="flex flex-col gap-[var(--space-1)] rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-[var(--space-3)]"
              >
                <Text className="m-0 text-[length:var(--text-body-sm)] leading-[var(--leading-normal)] text-[var(--text-primary)]">
                  {note.content}
                </Text>
                <div className="flex items-center justify-between gap-[var(--space-2)]">
                  <Text
                    component="span"
                    className="text-[length:var(--text-meta)] text-[var(--text-tertiary-soft)]"
                  >
                    {formatNoteAuthor(note.created_by)} · {formatNoteDate(note.created_at)}
                  </Text>
                  <UnstyledButton
                    type="button"
                    onClick={() => handleDelete(note.note_id)}
                    aria-label={t('detailView.notes.delete')}
                    data-testid={`delete-property-note-${note.note_id}`}
                    className="flex h-[24px] w-[24px] flex-none items-center justify-center rounded-(--radius-sm) text-[var(--text-tertiary-soft)] hover:bg-[var(--surface-hover)] hover:text-[var(--status-error-text)]"
                  >
                    <TrashIcon width={13} height={13} />
                  </UnstyledButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {composing && (
        <PropertyDialog
          title={t('detailView.notes.add')}
          width="480px"
          confirmLabel={t('detailView.notes.save')}
          onConfirm={handleSave}
          confirmDisabled={saving || draft.trim().length === 0}
          onClose={closeDialog}
          data-testid="property-note-dialog"
        >
          {/* `Textarea`, not `TextAreaInput`: the latter always renders
              `InputFill`, which reads a form store and throws outside a
              `StoreProvider`. */}
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('detailView.notes.placeholder')}
            data-testid="property-note-input"
            rows={5}
            className="w-full resize-y rounded-(--radius-md) border border-solid border-[var(--border-medium)] bg-[var(--surface-primary)] p-[var(--space-3)] text-[length:var(--text-body-sm)] leading-[var(--leading-normal)] text-[var(--text-primary)] [font-family:var(--font-family)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--seed-base)"
          />
        </PropertyDialog>
      )}
    </>
  );
};
